import { Close } from '@project-lary/react-material-symbols';
import { type ColumnDef, type Row, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useEffect, useState } from 'react';
import { cn } from '../utils/tailwind-merge';
import { DateRangePicker } from './date-range-picker';
import IconButton from './icon-button';
import MultiSelect, { type MultiSelectProps } from './multi-select';
import { DEFAULT_TABLE_PAGE, DataTablePagination, type DataTablePaginationProps, pageLimits } from './pagination';
import SearchBar, { type SearchBarProps } from './search-bar';
import SelectWithSearch, { type SelectWithSearchProps } from './select-with-search';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './table';

enum SelectType {
  SingleSelect = 'single',
  MultiSelect = 'multi',
}

interface SingleSelect {
  type: SelectType.SingleSelect;
  data: SelectWithSearchProps;
}

interface MultiSelectFilter {
  type: SelectType.MultiSelect;
  data: MultiSelectProps;
}

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  isLoading?: boolean;
  onRowClick?: (index: number) => void;
  pagination?: DataTablePaginationProps;
  search?: SearchBarProps;
  datePicker?: {
    fromDate?: string;
    toDate?: string;
    onDateChange?: (fromDate?: string, toDate?: string) => void;
  };
  classNames?: {
    table?: string;
    tableHeader?: string;
    tableBody?: string;
    tableRow?: string;
    tableCell?: string;
  };
  filter?: SingleSelect[] | MultiSelectFilter[];
  resetFilters?: () => void;
  noData?: string | React.ReactNode;
  CustomRow?: React.ForwardRefExoticComponent<
    React.HTMLAttributes<HTMLTableRowElement> & {
      row: Row<TData>;
    } & React.RefAttributes<HTMLTableRowElement>
  >;
}

export function DataTable<TData, TValue>({
  columns,
  data = [],
  isLoading,
  pagination,
  onRowClick,
  search,
  classNames,
  noData,
  datePicker,
  filter,
  resetFilters,
  CustomRow,
}: DataTableProps<TData, TValue>) {
  const [editedRows, setEditedRows] = useState([]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    meta: {
      editedRows,
      setEditedRows,
    },
  });

  const selectedItems = filter?.flatMap((item) => {
    if (item.type === SelectType.MultiSelect) {
      return item.data.selectedItems;
    }
    if (item.type === SelectType.SingleSelect) {
      return item.data.selectedItem ? [item.data.selectedItem] : [];
    }
    return [];
  });

  useEffect(() => {
    pagination?.onSetPage?.(DEFAULT_TABLE_PAGE);
  }, [search?.defaultValue, selectedItems?.length, datePicker?.fromDate, datePicker?.toDate]);

  return (
    <>
      <div className={cn('mb-4 overflow-visible rounded-lg border border-neutral-alpha-6 bg-white', classNames?.table)}>
        {search || filter || datePicker ? (
          <div className="flex items-center gap-2 p-3">
            {search ? (
              <div className="grow">
                <SearchBar {...search} />
              </div>
            ) : null}
            {filter?.length
              ? filter.map((item, index) =>
                  item.type === 'multi' ? (
                    <div key={index} className="shrink-0">
                      <MultiSelect
                        items={item.data.items}
                        placeholder={item.data.placeholder}
                        selectedItems={(item.data as MultiSelectProps)?.selectedItems}
                        onChange={(item.data as MultiSelectProps).onChange}
                        showSelectedItems={(item.data as MultiSelectProps).showSelectedItems}
                      />
                    </div>
                  ) : (
                    <div key={index} className="w-80 shrink-0">
                      <SelectWithSearch
                        items={item.data.items}
                        placeholder={item.data.placeholder}
                        onChange={(item.data as SelectWithSearchProps).onChange}
                        selectedItem={(item.data as SelectWithSearchProps).selectedItem}
                      />
                    </div>
                  ),
                )
              : null}

            {datePicker ? (
              <div className="shrink-0">
                <DateRangePicker
                  showCompare={false}
                  locale="de-DE"
                  onUpdate={(values) =>
                    datePicker?.onDateChange?.(values.range.from?.toISOString(), values.range.to?.toISOString())
                  }
                  initialDateFrom={datePicker.fromDate}
                  initialDateTo={datePicker.toDate}
                />
              </div>
            ) : null}
            {resetFilters ? (
              <div className="shrink-0">
                <IconButton
                  Icon={Close}
                  onClick={resetFilters}
                  className="flex size-10 cursor-pointer flex-col rounded-md bg-white text-text"
                />
              </div>
            ) : null}
          </div>
        ) : null}
        <Table>
          <TableHeader className={classNames?.tableHeader}>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead
                      key={header.id}
                      className={cn(header.column.columnDef.meta?.className ?? '', classNames?.tableHeader)}
                    >
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody className={classNames?.tableBody}>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => {
                if (CustomRow) {
                  return <CustomRow row={row} key={row.id} className={classNames?.tableRow} />;
                }

                return (
                  <TableRow
                    key={row.id}
                    onClick={() => onRowClick?.(row.index)}
                    data-state={row.getIsSelected() && 'selected'}
                    className={classNames?.tableRow}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        style={{
                          width: cell.column.getSize(),
                        }}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className={cn('h-24 text-center', classNames?.tableCell)}>
                  {isLoading ? 'Laden...' : noData || 'Keine Einträge vorhanden'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        {pagination && pagination.totalCount > pageLimits[0] ? <DataTablePagination {...pagination} /> : null}
      </div>
    </>
  );
}
